import { action, computed, observable } from 'mobx';
import { merge } from 'lodash';
import type { History } from 'history';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import {
    IPreviewContext,
    IPrintOrder,
    TPreviewTabId
} from '~/types/builder.types';
import { INITAL_PREVIEW_CONTEXT } from '~/components/Builder/Preview/BuilderPreview/useSetPreviewContext';
import { IPaidPaymentInfo } from '~/types/pay.types';
import { TSchedulerSlotError } from '~/components/ClientExperience/OneFlow/controllers/cx-schedule.controller';
import { trackError } from '~/services/logger/error.utils';
import { TFlowContext } from '~/types/model.types';
import { DEMO_LEAD_FORM_ID } from '~/constants/lead-forms.consts';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import { isPublicTemplateViewMode } from '~/utils/public-template-view-mode-flow.utils';
import { isEmbeddedInIframe } from '~/common/iframe.utils';

export enum FlowOwnerAbTest {
    chrage_block_fullname_field = 'charge_block_fullname_field'
}

export enum ErrorType {
    error = 'error',
    noPermission = 'HBNoPermissionOnFlowError',
    wrongFlowRevision = 'HBWrongFlowRevisionError',
    flowNotFound = 'HBFlowNotFoundError',
    slotUnavailable = 'slot_unavailable',
    sessionUnavailable = 'unavailable_session',
    sessionUpdated = 'session_updated',
    cardDeclined = 'HBStripeCardDeclinedError',
    wrongApiVersion = 'HBWrongAPIVersionError',
    clientNeedsToApproveChanges = 'HBClientNeedsToApproveChangesError',
    unauthenticated = 'HBUnauthenticatedError'
}

export enum IsWeakAuthenticatingReason {
    initialAuthCheck = 'initialAuthCheck',
    enterEmailAuthCheck = 'enterEmailAuthCheck'
}

export enum ForceStrongAuthReason {
    clientPortalAccess = 'clientPortalAccess',
    clientExperienceMemberAccessFromEmail = 'clientExperienceMemberAccessFromEmail',
    clientExperienceMemberAccessOnLand = 'clientExperienceMemberAccessOnLand',
    vendorAuthRaise = 'vendorAuthRaise'
}

export interface IPageSavedData {
    page_id: string;
    [variableId: string]: any;
}

export class FlowViewUiStore {
    rootStore: CXRootStore;

    @observable canvasWidth: number = Infinity;
    @observable enteredUserEmail: string;
    @observable error: ErrorType;
    @observable flowId: string = null;
    hasSeenInvoicePageTriggered: boolean = false;
    @observable isFetchingUserPaymentMethods: boolean = false;
    @observable isFormSubmitting: boolean;
    @observable isPDFPrinting: boolean = false;
    @observable isPaymentInProgress: boolean;
    @observable isThumbnailView: boolean = false;
    @observable pageSavedData: IPageSavedData;
    @observable previewContext: IPreviewContext = INITAL_PREVIEW_CONTEXT;
    @observable printOrder: IPrintOrder = null;
    @observable reloadFormikPropsCallback: () => void;
    @observable selectedFlowId: string;
    @observable selectedPageId: string;
    @observable shouldShowOutdatedFlowDialog: boolean;
    @observable shouldShowClientShareFlowModal: boolean;
    @observable isSignModalOpen: boolean;
    @observable shouldShowApproveSuccessProtip: boolean;
    @observable payInAdvanceMode: boolean;
    @observable paidPaymentInfo: IPaidPaymentInfo;
    @observable hasSeenContinueWithoutPaymentToast: boolean;
    @observable hasSeenStayAndScheduleOrRescheduleToast: boolean;
    @observable flowOwnerAbTest: Array<{ key: string; value: any }> = [];
    @observable isSlotUnavailableDialogOpen: boolean = false;
    @observable slotUnavailableDialogError: TSchedulerSlotError = null;
    @observable history: History<{}> = null;
    @observable isRescheduleMode: boolean = false;
    @observable isGoToSchedulePageModalOpen: boolean = false;
    @observable isEmbeddedWidget: boolean = false;
    @observable isPageSubmitting: boolean = false;
    @observable isLoadingClientPaymentPlan: boolean = false;
    @observable previewDisplayType: TPreviewTabId = 'Desktop';
    @observable isInIframePreview: boolean;
    @observable shouldShowPreviewOfTestVersion: boolean = false;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;

        this.isEmbeddedWidget = isEmbeddedInIframe();
    }

    @action.bound
    addFlowOwnerAbTest({ key, value }) {
        if (
            !this.flowOwnerAbTest.some(
                AbTest => AbTest.key === key && AbTest.value === value
            )
        ) {
            this.flowOwnerAbTest.push({ key, value });
        }
    }

    @action.bound
    setIsGoToSchedulePageModalOpen(value: boolean) {
        this.isGoToSchedulePageModalOpen = value;
    }

    @action.bound
    setHasSeenContinueWithoutPaymentToast(value: boolean) {
        this.hasSeenContinueWithoutPaymentToast = value;
    }

    @action.bound
    setHasSeenStayAndScheduleOrRescheduleToast(value: boolean) {
        this.hasSeenStayAndScheduleOrRescheduleToast = value;
    }

    @action.bound
    setPaidPaymentInfo(value: IPaidPaymentInfo) {
        this.paidPaymentInfo = value;
    }

    @action.bound
    setPreviewDisplayType(previewDisplayType: TPreviewTabId) {
        this.previewDisplayType = previewDisplayType;
    }

    @action.bound
    setPayInAdvanceMode(value) {
        this.payInAdvanceMode = value;
    }

    @action.bound
    setIsPDFPrinting(state: boolean) {
        this.isPDFPrinting = state;
    }

    @action.bound
    setPrintOrder(printOrder: IPrintOrder) {
        this.printOrder = printOrder;
    }

    @action.bound
    setIsFetchingUserPaymentMethods(state: boolean) {
        this.isFetchingUserPaymentMethods = state;
    }

    @action.bound
    setIsThumbnailView(state: boolean) {
        this.isThumbnailView = state;
    }

    @action.bound
    setPreviewContext(state: Partial<IPreviewContext>) {
        const newPreviewContext = { ...this.previewContext };
        this.previewContext = merge(newPreviewContext, state);
    }

    @action.bound
    setIsFormSubmitting(state: boolean) {
        this.isFormSubmitting = state;
    }

    @action.bound
    setIsPaymentInProgress(state: boolean) {
        this.isPaymentInProgress = state;
    }

    @action.bound
    setShouldShowOutdatedFlowDialog(state: boolean) {
        this.shouldShowOutdatedFlowDialog = state;
    }

    @action.bound
    setShouldShowClientShareFlowModal(state: boolean) {
        this.shouldShowClientShareFlowModal = state;
    }

    @action
    selectFlow(flowId: string) {
        this.selectedFlowId = flowId;
    }

    findPageIndex = (pageId: string) => {
        return this.selectedFlow.activeRevision.pages.findIndex(
            page => page.page_id === pageId
        );
    };

    @action.bound
    selectPage(pageId: string) {
        this.selectedPageId = pageId;
    }

    @action.bound
    setCanvasWidth({ width }) {
        this.canvasWidth = width;
    }

    @action
    setFlowId(flowId: string) {
        this.flowId = flowId;
    }

    @computed
    get isNewNavbarExperience() {
        return (
            this.selectedFlow?.isLeadForm &&
            this.rootStore.userStore.currUser.isNewNavbarExperience
        );
    }

    @computed
    get selectedPageIndex() {
        return this.findPageIndex(this.selectedPageId);
    }

    @action.bound
    setIsSignModalOpen(state: boolean) {
        this.isSignModalOpen = state;
    }
    @action.bound
    setShouldShowApproveSuccessProtip(state: boolean) {
        this.shouldShowApproveSuccessProtip = state;
    }

    @computed
    get hasSeenInvoicePage() {
        if (!this.selectedFlow.hasInvoiceBlock) {
            return false;
        }

        if (this.hasSeenInvoicePageTriggered) {
            return true;
        }

        const invoicePageIndex = this.selectedFlow.pages.findIndex(
            page => page.page_id === this.selectedFlow.pageWithInvoice.page_id
        );
        if (this.selectedPageIndex >= invoicePageIndex) {
            this.hasSeenInvoicePageTriggered = true;
            return true;
        }
        return false;
    }

    @computed
    get selectedFlow() {
        return this.rootStore.flowStore.flow;
    }

    @computed
    get isLeadFormWithLeadDetailsBlock() {
        return (
            this.selectedFlow.isLeadForm &&
            this.selectedFlow.hasPublicLeadDetailsBlock
        );
    }

    @computed
    get isFlowSubmitting() {
        return (
            (this.selectedPage.isLastPage && this.isFormSubmitting) ||
            this.isPageSubmitting
        );
    }

    @computed
    get selectedPage() {
        if (!this.selectedFlow) {
            return null;
        }
        return this.selectedFlow.activeRevision.pagesMap[this.selectedPageId];
    }

    @computed
    get isDemoLeadForm() {
        return this.selectedFlowId === DEMO_LEAD_FORM_ID;
    }

    get isViewerMode(): boolean {
        return isViewerMode();
    }

    get isPublicTemplateMode(): boolean {
        return isPublicTemplateViewMode();
    }

    get isViewOnlyMode(): boolean {
        return this.isPublicTemplateMode || this.isViewerMode;
    }

    @computed
    get flowContext(): TFlowContext {
        if (!this.selectedFlow) {
            return null;
        }
        if (this.selectedFlow.isLeadForm) {
            return 'publicFlow';
        }
        if (this.selectedFlow.isInstance) {
            return 'flowInstance';
        }
        if (this.selectedFlow.isTemplate) {
            return 'flowTemplate';
        }
        trackError({
            error: new Error(
                `Unknown builder context for flowId ${this.selectedFlowId}`
            )
        });
    }

    @action.bound
    getFlowOwnerAbTestByName(name) {
        return this.flowOwnerAbTest.find(abTest => abTest.key === name)?.value;
    }

    @action.bound
    setPageSavedData(pageData: IPageSavedData) {
        this.pageSavedData = pageData;
    }

    @action.bound
    setFormikReloadPropsCallback(loadFormikProps: () => void) {
        this.reloadFormikPropsCallback = loadFormikProps;
    }

    @action.bound
    setError(error: ErrorType) {
        this.error = error;
    }

    @action.bound
    setEnteredUserEmail(email: string) {
        this.enteredUserEmail = email;
    }

    @action.bound
    toggleSlotUnavailableDialog(error?: TSchedulerSlotError) {
        this.isSlotUnavailableDialogOpen = !this.isSlotUnavailableDialogOpen;
        this.slotUnavailableDialogError = error;
    }

    @action.bound
    setIsRescheduleMode(state: boolean) {
        this.isRescheduleMode = state;
    }

    @action.bound
    setHistory(history: History<{}>) {
        this.history = history;
    }

    @action.bound
    setIsPageSubmitting(state: boolean) {
        this.isPageSubmitting = state;
    }

    @action.bound
    setIsLoadingClientPaymentPlan(state: boolean) {
        this.isLoadingClientPaymentPlan = state;
    }

    @action.bound
    setIsInIframePreview(isInIframePreview: boolean) {
        this.isInIframePreview = isInIframePreview;
    }
    @action.bound
    setShouldShowPreviewOfTestVersion(shouldShowPreviewOfTestVersion: boolean) {
        this.shouldShowPreviewOfTestVersion = shouldShowPreviewOfTestVersion;
    }

    @computed
    get isCTAButtonDisabled() {
        const {
            isFormSubmitting,
            selectedFlow: { isExpiredOrExpiredPreview }
        } = this;

        return isFormSubmitting || isExpiredOrExpiredPreview;
    }

    @computed
    get blocksToApproveOnPage() {
        const { selectedFlow } = this;
        return selectedFlow.sortedPendingApprovalBlocks.filter(
            pendingBlock => pendingBlock.page_id === this.selectedPageId
        );
    }

    @computed
    get vendorEmail() {
        const { selectedFlow } = this;
        const { currCompany } = this.rootStore.companyStore;
        return selectedFlow?.vendor?.email || currCompany?.email;
    }

    @action.bound
    reset() {
        this.setFlowId(null);
        this.selectFlow(null);
        this.selectPage(null);
        this.setIsFormSubmitting(false);
        this.setIsPDFPrinting(false);
    }
}
